import React, { useEffect, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

interface BulletListEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}

const BulletListEditor: React.FC<BulletListEditorProps> = ({
  value,
  onChange,
  placeholder = 'Add discount details...',
  className = ''
}) => {
  const [isFocused, setIsFocused] = useState(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        orderedList: false,
        codeBlock: false,
        blockquote: false,
        heading: false
      })
    ],
    content:
      value && value.trim().length > 0
        ? `<ul>${value
            .split('\n')
            .filter(item => item.trim() !== '')
            .map(item => `<li><p>${item}</p></li>`)
            .join('')}</ul>`
        : '<ul><li><p></p></li></ul>',
    editorProps: {
      attributes: {
        class: `focus:outline-none min-h-[100px] ${className}`
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()

      // Ensure it's always a list structure even if user deletes everything
      if (!html.includes('<li>')) {
        editor.commands.setContent('<ul><li><p></p></li></ul>')
      }

      // Parse the HTML to extract list items
      const parser = new DOMParser()
      const doc = parser.parseFromString(editor.getHTML(), 'text/html')
      const items = Array.from(doc.querySelectorAll('li'))
        .map(li => li.textContent?.trim() || '')
        .filter(text => text !== '')

      onChange(items.join('\n'))
    },
    onFocus: ({ editor }) => {
      setIsFocused(true)
      // If empty and no list item, add one to start editing
      if (editor.isEmpty && !editor.getHTML().includes('<li>')) {
        editor.commands.setContent('<ul><li><p></p></li></ul>')
      }
    },
    onBlur: () => setIsFocused(false),
    immediatelyRender: false
  })

  useEffect(() => {
    if (!editor || editor.isFocused) return

    const currentItemsText = Array.from(
      new DOMParser().parseFromString(editor.getHTML(), 'text/html').querySelectorAll('li')
    )
      .map(li => li.textContent?.trim() || '')
      .filter(text => text !== '')
      .join('\n')

    if (currentItemsText !== value) {
      const newHtml =
        value && value.trim().length > 0
          ? `<ul>${value
              .split('\n')
              .filter(item => item.trim() !== '')
              .map(item => `<li><p>${item}</p></li>`)
              .join('')}</ul>`
          : '<ul><li><p></p></li></ul>'
      editor.commands.setContent(newHtml, false)
    }
  }, [value, editor])

  const isEmpty = editor ? editor.getText().trim() === '' : true

  return (
    <div className='relative w-full bullet-list-editor-container'>
      <style>
        {`
          .bullet-list-editor-container .ProseMirror ul {
            list-style-type: disc;
            padding-left: 1.25rem;
            margin: 0;
          }
          .bullet-list-editor-container .ProseMirror li {
            margin: 0;
            color: inherit;
          }
          .bullet-list-editor-container .ProseMirror p {
            margin: 0;
          }
        `}
      </style>

      {isEmpty && !isFocused && (
        <div className='absolute top-0 left-5 text-zinc-400 pointer-events-none text-text-sm-regular'>
          {placeholder}
        </div>
      )}

      <EditorContent editor={editor} />
    </div>
  )
}

export default BulletListEditor
